// The note, as text.
//
// Every character comes from `body` and is rendered as a React text node, so
// markup an EMR left in the note shows up as the characters an EMR left in the
// note. There is no dangerouslySetInnerHTML anywhere in this feature and there
// must never be one: these notes come from four third-party systems and are
// written by people typing into them.
//
// Highlights are given as absolute ranges into `body` (see noteText.js), never
// as a rewritten string, so a suggestion's quote lands on the sentence it
// actually came from.

import React from "react";

// Sort, then drop anything overlapping what has already been taken: the first
// range wins, so a search match inside a selected quote is absorbed by the quote
// rather than rendering a nested mark.
function flatten(ranges) {
  const ordered = [...(ranges || [])]
    .filter((r) => r && r.end > r.start)
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const out = [];
  let taken = -1;
  ordered.forEach((r) => {
    if (r.start < taken) return;
    out.push(r);
    taken = r.end;
  });
  return out;
}

// The slice [from, to) of `body`, with the ranges inside it marked.
function run(body, from, to, ranges, keyPrefix) {
  const nodes = [];
  let cursor = from;
  ranges.forEach((r, i) => {
    if (r.end <= from || r.start >= to) return;
    const start = Math.max(r.start, from);
    const end = Math.min(r.end, to);
    if (start > cursor) nodes.push(body.slice(cursor, start));
    nodes.push(
      <mark
        key={`${keyPrefix}-${i}`}
        className={`nv-mark nv-mark-${r.kind || "match"}${r.active ? " nv-mark-active" : ""}`}
        data-mark-kind={r.kind || "match"}
        data-mark-active={r.active ? "yes" : "no"}
      >
        {body.slice(start, end)}
      </mark>,
    );
    cursor = end;
  });
  if (cursor < to) nodes.push(body.slice(cursor, to));
  return nodes;
}

export default function NoteBody({ body, segments, ranges }) {
  const flat = flatten(ranges);
  return (
    <div className="nv-body" data-note-body>
      {segments.map((segment, index) => {
        const labelEnd = segment.label
          ? segment.start + segment.label.length
          : segment.start;
        return (
          <p
            key={`${segment.start}-${segment.end}`}
            className={`nv-block${segment.heading ? " nv-block-heading" : ""}${segment.heading && !segment.label ? " nv-block-caps" : ""}`}
            // Found by query rather than held in a ref: a fresh ref callback per
            // paragraph per render costs a detach and re-attach on every search
            // keystroke, times however many paragraphs the note has.
            data-segment={index}
          >
            {segment.label && (
              <strong className="nv-label">
                {run(body, segment.start, labelEnd, flat, `l${index}`)}
              </strong>
            )}
            {run(body, labelEnd, segment.end, flat, `b${index}`)}
          </p>
        );
      })}
    </div>
  );
}
